import {
  Box,
  Button,
  Container,
  Heading,
  Input,
  Text,
  SimpleGrid,
  Image,
  Textarea,
  Stack,
  VStack,
  IconButton,
  HStack,
  useToast,
  Badge,
  useColorModeValue,
  ScaleFade,
  Flex,
  Icon,
  Divider,
  Code,
  useClipboard,
} from "@chakra-ui/react";
import { 
  AddIcon, 
  DeleteIcon, 
  EditIcon,
  ViewIcon,
  CopyIcon,
  CheckIcon,
} from "@chakra-ui/icons";
import { 
  FiHome, 
  FiPlus, 
  FiCode, 
  FiZap,
  FiLayers,
  FiTarget,
  FiChrome,
  FiMessageSquare,
  FiGift,
  FiYoutube,
} from "react-icons/fi";
import { useState } from "react";
import { ReactComponent as SaweriaIcon } from "../assets/iconsweria.svg";

export default function PromptJson() {
  const [subject, setSubject] = useState("");
  const [extras, setExtras] = useState([]);
  const [jsonResult, setJsonResult] = useState("");
  const toast = useToast();
  const { hasCopied, onCopy } = useClipboard(jsonResult);

  // Color mode values
  const bgGradient = useColorModeValue(
    "linear(to-br, blue.50, blue.50, purple.50)",
    "linear(to-br, gray.900, teal.900, blue.900)"
  );
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const inputBg = useColorModeValue("gray.50", "gray.700");
  const fieldBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const borderColorFocus = useColorModeValue("white", "gray.700");

  /* ---------- handlers ---------- */
  const addExtra = () => {
    const newExtra = { 
      id: Date.now(), 
      title: "Detail", 
      value: "" 
    };
    setExtras([...extras, newExtra]);
    toast({
      title: "Field Ditambahkan",
      description: "Field detail baru berhasil ditambahkan",
      status: "success",
      duration: 2000,
      isClosable: true,
      position: "bottom-right",
    });
  };

  const updateExtra = (id, field, val) =>
    setExtras(extras.map((e) => (e.id === id ? { ...e, [field]: val } : e)));

  const removeExtra = (id) => {
    setExtras(extras.filter((e) => e.id !== id));
    toast({
      title: "Field Dihapus",
      description: "Field detail berhasil dihapus",
      status: "info",
      duration: 2000,
      isClosable: true,
      position: "bottom-right",
    });
  };

  const editTitle = (id) => {
    const ex = extras.find((e) => e.id === id);
    const newTitle = prompt("Masukkan judul baru:", ex.title);
    if (newTitle && newTitle.trim()) {
      updateExtra(id, "title", newTitle.trim());
    }
  };

  const generate = () => {
    if (!subject.trim()) {
      toast({
        title: "Subjek Diperlukan",
        description: "Mohon masukkan subjek prompt terlebih dahulu",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    const json = { 
      subject, 
      ...extras.reduce((a, e) => ({ ...a, [e.title]: e.value }), {}) 
    };
    setJsonResult(JSON.stringify(json, null, 2));
    
    toast({
      title: "JSON Generated!",
      description: "Prompt JSON berhasil dibuat dan siap untuk dicopy",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  };

  const handleCopy = () => {
    onCopy();
    toast({
      title: "Berhasil Dicopy!",
      description: "JSON prompt telah disalin ke clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
      position: "bottom-right",
    });
  };

  return (
    <Box 
      bgGradient={bgGradient}
      minH="100vh" 
      py={12}
      position="relative"
      overflow="hidden"
    >
      {/* Background decorations */}
      <Box
        position="absolute"
        top="-100px"
        left="-100px"
        width="300px"
        height="300px"
        borderRadius="full"
        bgGradient="linear(to-br, teal.300, blue.300)"
        opacity={0.1}
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom="-50px"
        right="-50px"
        width="200px"
        height="200px"
        borderRadius="full"
        bgGradient="linear(to-tl, purple.300, pink.300)"
        opacity={0.1}
        zIndex={0}
      />

      <Container maxW="4xl" position="relative" zIndex={1}>
        <ScaleFade initialScale={0.9} in={true}>
          <VStack spacing={10} w="full">
            {/* Header Section */}
            <Box textAlign="center" w="full">
              <Flex
                justify="space-between"
                align="center" // Memastikan tombol terletak di tengah pada mobile
                mb={6}
                flexWrap="wrap" // Menambahkan flex-wrap untuk mencegah tombol turun
              >
                <VStack align="start" spacing={3} flex={1} maxW="full">
                  <Badge
                    colorScheme="blue"
                    variant="subtle"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                  >
                    <Icon as={FiGift} mr={2} />
                    Wedding Generator
                  </Badge>
                  <Heading
                    color={textColor}
                    fontSize={{ base: "3xl", md: "5xl" }}
                    fontWeight="900"
                    bgGradient="linear(to-r, blue.500, purple.500, teal.500,)"
                    bgClip="text"
                    lineHeight="shorter"
                  >
                    Undangan Digital Pernikahan
                  </Heading>
                  <Text
                    color={mutedColor}
                    fontSize={{ base: "lg", md: "xl" }}
                    maxW="lg"
                    lineHeight="tall"
                  >
                    Buat undangan digital pernikahan
                  </Text>
                </VStack>

                {/* Tombol Section */}
                <Box
                  display="flex"
                  gap={4}
                  flexWrap="wrap" // Menambahkan wrap untuk tombol
                  justify="center" // Menambahkan center untuk meratakan tombol
                  w="full" // Membuat tombol tetap responsif
                >


                  {/* Tombol 1 */}
                  <a
                    href="https://laimonprompt.blogspot.com/"
                    target="_self"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="md"
                      variant="ghost"
                      colorScheme="blue"
                      leftIcon={<Icon as={FiHome} />}
                      _hover={{
                        bg: "blue.50",
                        transform: "translateY(-2px)",
                        shadow: "lg",
                      }}
                      transition="all 0.3s ease"
                      w={{ base: "100%", sm: "auto" }} // Tombol full width pada mobile
                    >
                      Home
                    </Button>
                  </a>
                  {/* Tombol 2 */}
                  <a
                    href="https://www.tiktok.com/@laimondev"
                    target="_self"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="md"
                      variant="ghost"
                      colorScheme="blue"
                      leftIcon={<Icon as={FiYoutube} />}
                      _hover={{
                        bg: "blue.50",
                        transform: "translateY(-2px)",
                        shadow: "lg",
                      }}
                      transition="all 0.3s ease"
                      w={{ base: "100%", sm: "auto" }} // Tombol full width pada mobile
                    >
                      Tutorial
                    </Button>
                  </a>
                  {/* Tombol 3 */}
                  <a
                    href="https://saweria.co/laimondev/"
                    target="_self"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="md"
                      variant="ghost"
                      colorScheme="blue"
                      leftIcon={<SaweriaIcon width="20px" height="20px" />}
                      _hover={{
                        bg: "blue.50",
                        transform: "translateY(-2px)",
                        shadow: "lg",
                      }}
                      transition="all 0.3s ease"
                      w={{ base: "100%", sm: "auto" }} // Tombol full width pada mobile
                    >
                      Saweria
                    </Button>
                  </a>


                </Box>
              </Flex>
            </Box>

         <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
  {/* Grid Item 1 */}
  <Box
    bg="white"
    borderRadius="3xl"
    shadow="2xl"
    overflow="hidden"
    border="1px solid"
    borderColor="gray.200"
  >
    <Image
      src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEi4xFHhdXfXWsypzmGYURTEPPagMSD6v10K2mfK0qwgW-z0pKmHnX4rkCOTAdZWuMCSxoQzwlxmURyheWAjiq2IH3i9a9GMshuk22JjAm1KXtIsn53ByjpgaAtNRgmIvYf7l6TzdTvsBbvAx3IO-MMZpk0MNuudISTvJmRYIXyacCnjrrXGS8tsVgrE680P/s4000/xasxa.png"
      alt="Preview 1"
      w="full"
      h="300px"
      objectFit="cover"
    />
    <VStack p={6} spacing={4} alignItems="stretch" w="full">
      <Heading size="md" textAlign="center" color={"blue.500"}>Union of Hearts</Heading> {/* Menambahkan Judul */}
      <a href="https://union-of-hearts-six.vercel.app/weddinggenerator" target="_blank" rel="noopener noreferrer">
        <Button
          leftIcon={<EditIcon />}
          colorScheme="blue"
          variant="solid"
          w="full"
        >
          Edit
        </Button>
      </a>
      <a href="https://stellar-salamander-46d8a4.netlify.app/laimondev-wedding-website" target="_blank" rel="noopener noreferrer">
        <Button
          leftIcon={<ViewIcon />}
          colorScheme="blue"
          variant="outline"
          w="full"
        >
          Preview
        </Button>
      </a>
    </VStack>
  </Box>

  {/* Grid Item 2 */}
  <Box
    bg="white"
    borderRadius="3xl"
    shadow="2xl"
    overflow="hidden"
    border="1px solid"
    borderColor="gray.200"
  >
    <Image
      src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEj1Nl7qql4HVBS9mhXNx6APBJ4YE_R772adPrQlZdvfqP4_OceDcl_NCqu0hVWLC3hTPM-lCgeYwSRbLLLu2WfSteKVcp0cVF0CD4xB7-IQYKPhdWkbKYdPv5LUsDP3RwDa6hES4HOV5FBXwATXUiT7Vs81eBebWEOWvCcVZdynbInKLVKPobCtobB5D7fg/s4000/asas.png"
      alt="Preview 2"
      w="full"
      h="300px"
      objectFit="cover"
    />
    <VStack p={6} spacing={4} alignItems="stretch" w="full">
      <Heading size="md" textAlign="center" color={"blue.500"}>Beneath The Blue Sky</Heading> {/* Menambahkan Judul */}
      <a href="https://beneath-the-blue-sky.vercel.app/weddinggenerator" target="_blank" rel="noopener noreferrer">
        <Button
          leftIcon={<EditIcon />}
          colorScheme="blue"
          variant="solid"
          w="full"
        >
          Edit
        </Button>
      </a>
      <a href="https://stellar-salamander-46d8a4.netlify.app/laimondev-wedding-website-masculine-blue" target="_blank" rel="noopener noreferrer">
        <Button
          leftIcon={<ViewIcon />}
          colorScheme="blue"
          variant="outline"
          w="full"
        >
          Preview
        </Button>
      </a>
    </VStack>
  </Box>
</SimpleGrid>


          </VStack>
        </ScaleFade>
      </Container>
    </Box>
  );
}